import React, { useState } from "react"
import { capitalize } from "humanize-plus"
import classnames from "classnames"
import parse from "html-react-parser"
import { GraphError } from "javascripts/types"
import { verifyPhoneNumber } from "shared/lib/verify_phone_number"
import { verifyEmailAddress } from "shared/lib/verify_email_address"
import { sendPin, activateWithPin } from "./api"

import ErrorIcon from "images/icons/icon-error-triangle.svg?raw"
import CheckIcon from "images/components/check2.svg?raw"

import styles from "./activation.module.scss"

import {
  trackServiceConnectionStarted,
  trackServiceConnectionCompleted,
  trackServiceConnectionFailed,
} from "app/utils/mixpanel_tracking"

interface Props {
  service: {
    name: string
    module_name: string
  }
  liveChannelId?: string
  liveChannelFields?: {
    [key: string]: string
  }
  successUrl: string
}

const ActivateServiceWithPin = ({ service, liveChannelId, liveChannelFields, successUrl }: Props) => {
  let channelField = "phone_number"
  if (service.module_name.match(/email/)) channelField = "email_address"

  const fieldName = channelField.replace(/_/, " ")
  const isNewConnection = !liveChannelId
  const connectionContext = "manual"
  const entrypoint = "service_settings"
  const authType = "pin"
  const fieldValue = liveChannelFields ? liveChannelFields[channelField] : ""

  const [identifier, setIdentifier] = useState<string>(fieldValue || "")
  const [pinSent, setPinSent] = useState<boolean>(false)
  const [pinRetried, setPinRetried] = useState<boolean>(false)
  const [activationSent, setActivationSent] = useState<boolean>(false)
  const [pin, setPin] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [smsConsent, setSmsConsent] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)

  const handleIdentifierChange = (e: Event) => {
    const identifier = (e.target as HTMLInputElement)?.value
    setIdentifier(identifier)

    setError("")
    setPinSent(false)
    setPinRetried(false)
    setActivationSent(false)
    setPin("")
  }

  const handlePinChange = (e: Event) => {
    const pin = (e.target as HTMLInputElement).value
    setPin(pin)
  }

  const handleSendPin = async (e: Event) => {
    e.preventDefault()

    if (channelField === "email_address") {
      if (!verifyEmailAddress(identifier)) {
        setError("Invalid email address")
        return
      }
    } else {
      if (!verifyPhoneNumber(identifier)) {
        setError("Invalid phone number")
        return
      }
    }

    await sendPin(service.module_name, identifier)
    setPinSent(true)
  }

  const handleRetry = () => {
    if (pinRetried) return

    sendPin(service.module_name, identifier)

    setPinRetried(true)
    setActivationSent(false)
    setError("")
  }

  const handlePinSubmit = async (e: Event) => {
    e.preventDefault()
    setActivationSent(true)
    try {
      const startTime = Date.now()

      trackServiceConnectionStarted({
        serviceName: service.name,
        serviceModuleName: service.module_name,
        authType,
        connectionContext,
        isNewConnection,
        entrypoint,
      })

      const response = await activateWithPin(service.module_name, identifier, pin)
      if (response.newPinActivateLiveChannel.status === "successful") {
        trackServiceConnectionCompleted({
          serviceName: service.name,
          serviceModuleName: service.module_name,
          authType,
          connectionContext,
          isNewConnection,
          inDiyFlow: false,
          entrypoint,
          timeToConnect: Date.now() - startTime,
        })

        window.location.assign(successUrl)
      } else {
        // expected to be invalid pin message
        trackServiceConnectionFailed({
          serviceName: service.name,
          serviceModuleName: service.module_name,
          authType,
          connectionContext,
          isNewConnection,
          entrypoint,
          errorType: "invalid_pin",
        })
        setError(response.newPinActivateLiveChannel.errors[0].message)
        setActivationSent(false)
      }
    } catch (e) {
      trackServiceConnectionFailed({
        serviceName: service.name,
        serviceModuleName: service.module_name,
        authType,
        connectionContext,
        isNewConnection,
        entrypoint,
        errorType: (e as GraphError).details || "pin_activation_failed",
      })
      setError((e as GraphError).details || "Unexpected failure to activate")
    }
  }

  const isSmsService = service.module_name.match(/sms/)

  return (
    <div className={styles.activation}>
      <div className={styles["service-fields"]}>
        <h1>
          {liveChannelId ? "Reconnect" : "Connect"} to {service.name}
        </h1>
        <div className={styles["service-activation-form"]}>
          <form className="new_live_channel" id="new_live_channel" onSubmit={pinSent ? handlePinSubmit : handleSendPin}>
            <div className={styles["live-channel-fields"]}>
              {isSmsService ? (
                <>
                  <p>
                    Enter the {fieldName} you would like to use for all of your {service.name} Applets.
                  </p>
                  <label htmlFor="channel-field">{capitalize(fieldName)}</label>
                  <input
                    id="channel-field"
                    className={classnames({ [styles["after-send"]]: pinSent && !pin})}
                    type={channelField === "email_address" ? "email" : "tel"}
                    value={identifier}
                    placeholder={channelField === "email_address" ? "someone@example.com" : "1 555 555 5555"}
                    onChange={handleIdentifierChange}
                    required
                  />
                  <div>
                    <p>
                      By providing your phone number you agree to receive informational text messages from IFTTT.
                      Message frequency will vary. Message and data rates may apply. Reply stop to cancel.
                    </p>

                    <div>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent((e.target as HTMLInputElement).checked)}
                        />
                        <span></span>
                        <span>I would like to receive SMS messages from IFTTT</span>
                      </label>

                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted((e.target as HTMLInputElement).checked)}
                        />
                        <span></span>
                        <span>
                          I accept IFTTT's <a href="/terms" target="_blank">terms of service and privacy policy</a>
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Enter the {fieldName} you would like to use for all of your {service.name} Applets.
                  </p>
                  <label htmlFor="channel-field">{capitalize(fieldName)}</label>
                  <input
                    id="channel-field"
                    className={classnames({ [styles["after-send"]]: pinSent && !pin})}
                    type={channelField === "email_address" ? "email" : "tel"}
                    value={identifier}
                    placeholder={channelField === "email_address" ? "someone@example.com" : "1 555 555 5555"}
                    onChange={handleIdentifierChange}
                    required
                  />
                </>
              )}

              {error && (
                <div className={`${styles["after-message"]} ${styles.submit}`}>
                  <span>{parse(ErrorIcon)}</span>
                  {error}
                </div>
              )}
              {pinSent && !pin && !error && (
                <div className={`${styles["after-message"]} ${styles.send}`}>
                  <span>{parse(CheckIcon)}</span>
                  Pin sent to your {fieldName}
                </div>
              )}
              {pinSent ? (
                <>
                  <div className={classnames(styles["activation-field-entry"], { [styles.error]: !!error })}>
                    <label htmlFor="pin">Please enter the PIN you received below</label>
                    <input
                      type="text"
                      name="pin"
                      id="pin"
                      value={pin}
                      onChange={handlePinChange}
                      placeholder="4-digit PIN"
                      data-cobrowse-redacted
                    />
                  </div>
                  {error && (
                    <div className={`${styles["after-message"]} ${styles.submit}`}>
                      <span>{parse(ErrorIcon)}</span>
                      Invalid PIN
                    </div>
                  )}
                  <div className={styles["horizontal-ctas"]}>
                    <button
                      className={classnames("link-inline", { [styles.disabled]: pinRetried })}
                      type="button"
                      onClick={handleRetry}
                      disabled={pinRetried}
                    >
                      {pinRetried ? "PIN Resent" : "Retry"}
                    </button>
                    <input
                      className="button-tertiary"
                      type="submit"
                      name="commit"
                      value={liveChannelId ? "Update" : "Connect"}
                      disabled={activationSent || pin.length !== 4}
                      onSubmit={handlePinSubmit}
                    />
                  </div>
                </>
              ) : (
                <div className={styles["send-pin"]}>
                  <button
                    className="button-secondary"
                    onClick={handleSendPin}
                    disabled={isSmsService && (!smsConsent || !termsAccepted)}
                  >
                    Send PIN
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ActivateServiceWithPin
